import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface DropdownItem {
  label: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

/** Dropdown/Menu — 경량(의존성 없음). click-outside + ESC 닫힘, role=menu. */
export function Dropdown({ trigger, items, align = 'right', className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={['bq-dropdown', className ?? ''].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="bq-dropdown__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open && (
        <div className={`bq-dropdown__menu bq-dropdown__menu--${align}`} role="menu">
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`bq-dropdown__item${it.danger ? ' is-danger' : ''}`}
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onClick?.(); }}
            >
              {it.icon && <span className="bq-dropdown__icon" aria-hidden="true">{it.icon}</span>}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
